import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './SignUpPage.css';

// Import images
import cdcLogo from '../../../assets/icons/cdc-logo.png';
import { ArrowLeftBlack } from '../../../assets/icons';
import AlertBox from '../../../components/AlertBox/AlertBox';

// Import Redux actions
import RegisterActions from '../../../redux/RegisterRedux';
import { RegisterSelectors } from '../../../redux/RegisterRedux';

// Back Arrow Icon Component
const BackArrowIcon: React.FC = () => (
  <ArrowLeftBlack />
);

// CDC Logo Component
const CDCLogo: React.FC = () => (
  <div className="cdc-logo">
    <img 
      src={cdcLogo} 
      alt="CDC Logo" 
      className="cdc-logo-image"
    />
  </div>
);

interface SignUpPageState {
  email: string;
  childName: string;
  parentName: string;
  relation: string;
  emailError: string | null;
  childNameError: string | null;
  parentNameError: string | null;
  relationError: string | null;
  props?: SignUpPageProps;
  registerSuccessPopupOpen?: boolean;
}

interface SignUpPageProps extends WithTranslation {
  // Redux props
  registerFetching?: boolean;
  registerSuccess?: boolean;
  registerError?: string | null;
  registerStatus?: any;
  register?: (email: string, childName: string, parentName: string, relation: string) => void;
  resetData?: () => void;
  // Navigation props
  navigate?: (path: string) => void;
  goBack?: () => void;
}

class SignUpPage extends Component<SignUpPageProps, SignUpPageState> {
  constructor(props: SignUpPageProps) {
    super(props);
    this.state = {
      email: '',
      childName: '',
      parentName: '',
      relation: '',
      emailError: null,
      childNameError: null,
      parentNameError: null,
      relationError: null,
      registerSuccessPopupOpen: false,
    };
  }

  // Handle error display
  componentDidUpdate(prevProps: SignUpPageProps) {
    if (this.props.registerError && this.props.registerError !== prevProps.registerError) {
      this.showErrorAlert(this.props.t('sign-up-screen:alert.fail.message'));
    }
  }

  // Implement back navigation
  back() {
    if (this.props.goBack) {
      this.props.goBack();
    } else {
      // Fallback to browser back
      window.history.back();
    }
  }

  // Implement sign up functionality
  _onSignUpPress() {
    if (this.props.register) {
      this.props.register(
        this.state.email,
        this.state.childName,
        this.state.parentName,
        this.state.relation
      );
    }
  }

  // Custom error alert
  showErrorAlert = (message: string) => {
    // Create a custom alert element
    const alertDiv = document.createElement('div');
    alertDiv.className = 'custom-alert';
    alertDiv.innerHTML = `
      <div class="alert-content">
        <div class="alert-title">Missing Information</div>
        <div class="alert-message">${message}</div>
        <button class="alert-button" onclick="this.parentElement.parentElement.remove()">OK</button>
      </div>
    `;
    
    // Add styles
    alertDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    `;
    
    // Add to body
    document.body.appendChild(alertDiv);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      if (alertDiv.parentElement) {
        alertDiv.remove();
      }
    }, 5000);
  }

  // Custom success alert with navigation
  showSuccessAlert = (message: string) => {
    // Create a custom alert element
    const alertDiv = document.createElement('div');
    alertDiv.className = 'custom-alert';
    alertDiv.innerHTML = `
      <div class="alert-content">
        <div class="alert-message">${message}</div>
        <button class="alert-button" onclick="this.parentElement.parentElement.remove(); window.location.href='/login'">OK</button>
      </div>
    `;
    
    // Add styles
    alertDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    `;
    
    // Add to body
    document.body.appendChild(alertDiv);
  }

  // Implement getDerivedStateFromProps for Redux state handling
  static getDerivedStateFromProps(nextProps: SignUpPageProps, prevState: SignUpPageState) {
    if (prevState.props) {
      if (!nextProps.registerFetching) {
        if (nextProps.registerSuccess && nextProps.registerSuccess !== prevState.props.registerSuccess) {
          // Show success message and navigate to login
          /* setTimeout(() => {
            if (nextProps.navigate) {
              nextProps.navigate('/login');
            } else {
              window.location.href = '/login';
            }
          }, 2000); */
          
          return {
            email: '',
            childName: '',
            parentName: '',
            relation: '',
            registerSuccessPopupOpen: true,
          };
        }

        if (nextProps.registerError && nextProps.registerError !== prevState.props.registerError) {
          // Error handling is done in componentDidUpdate
          console.error('Registration error:', nextProps.registerError);
        }
      }
    }

    return {
      props: nextProps,
    };
  }

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: e.target.value });
  };

  handleChildNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ childName: e.target.value });
  };

  handleParentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ parentName: e.target.value });
  };

  handleRelationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ relation: e.target.value });
  };

  // Form validation
  validateEmail = (email: string): string | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  validateChildName = (childName: string): string | null => {
    if (!childName) {
      return 'Child\'s name is required';
    }
    if (childName.length < 2) {
      return 'Child\'s name must be at least 2 characters';
    }
    return null;
  };

  validateParentName = (parentName: string): string | null => {
    if (!parentName) {
      return 'Parent\'s name is required';
    }
    if (parentName.length < 2) {
      return 'Parent\'s name must be at least 2 characters';
    }
    return null;
  };

  validateRelation = (relation: string): string | null => {
    if (!relation) {
      return 'Relationship is required';
    }
    if (relation.length < 2) {
      return 'Relationship must be at least 2 characters';
    }
    return null;
  };

  handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const emailError = this.validateEmail(this.state.email);
    const childNameError = this.validateChildName(this.state.childName);
    const parentNameError = this.validateParentName(this.state.parentName);
    const relationError = this.validateRelation(this.state.relation);
    
    this.setState({
      emailError,
      childNameError,
      parentNameError,
      relationError,
    });
    
    // If any errors, show alert and return
    if (emailError || childNameError || parentNameError || relationError) {
      this.showErrorAlert(this.props.t('sign-up-screen:alert.input-missing.message'));
      return;
    }
    
    // If no errors, proceed with registration
    this._onSignUpPress();
  };

  registerSuccessPopupOpen = () => {
    this.setState({
      registerSuccessPopupOpen: true,
    });
  };

  registerSuccessPopupClose = () => {
    this.setState({
      registerSuccessPopupOpen: false,
    }, () => {
      if (this.props.navigate) {
        this.props.navigate('/login');
      }else{
        window.location.href = '/login';
      }
    });
  }

  handleBackClick = () => {
    this.back();
  };

  componentDidMount(){
    if (this.props.resetData) {
      this.props.resetData();
    }
  }

  render() {
    const { email, childName, parentName, relation, emailError, childNameError, parentNameError, relationError } = this.state;
    const { t, registerFetching, registerSuccess, registerError } = this.props;

    return (
      <div className="signup-page">
        {/* Header */}
        <header className="signup-header">
          <div className="header-left">
            <button 
              className="back-button"
              onClick={this.handleBackClick}
              type="button"
            >
              <BackArrowIcon />
            </button>
          </div>
          <div className="header-body"></div>
          <div className="header-right"></div>
        </header>

        {/* Main Content */}
        <div className="signup-body">
          <div className="signup-container">
            {/* Logo Section */}
            <div className="logo-container">
              <CDCLogo />
            </div>

            {/* Form Section */}
            <form className="form-container" onSubmit={this.handleSubmit}>
              {/* Email Input */}
              <div className="form-group">
                <label className="form-label uppercase">
                  {t('sign-up-screen:form.label.email')}
                </label>
                <div className="input-item">
                  <input
                    type="email"
                    className={`form-input ${emailError ? 'error' : ''}`}
                    placeholder={t('sign-up-screen:form.placeholder.email')}
                    value={email}
                    onChange={this.handleEmailChange}
                    autoCapitalize="none"
                    required
                  />
                </div>
              </div>

              {/* Child's Name Input */}
              <div className="form-group">
                <label className="form-label uppercase">
                  {t('sign-up-screen:form.label.childs-name')}
                </label>
                <div className="input-item">
                  <input
                    type="text"
                    className={`form-input ${childNameError ? 'error' : ''}`}
                    placeholder={t('sign-up-screen:form.placeholder.childs-name')}
                    value={childName}
                    onChange={this.handleChildNameChange}
                    required
                  />
                </div>
              </div>

              {/* Parent's Name Input */}
              <div className="form-group">
                <label className="form-label uppercase">
                  {t('sign-up-screen:form.label.parents-name')}
                </label>
                <div className="input-item">
                  <input
                    type="text"
                    className={`form-input ${parentNameError ? 'error' : ''}`}
                    placeholder={t('sign-up-screen:form.placeholder.parents-name')}
                    value={parentName}
                    onChange={this.handleParentNameChange}
                    required
                  />
                </div>
              </div>

              {/* Relationship Input */}
              <div className="form-group">
                <label className="form-label uppercase">
                  {t('sign-up-screen:form.label.relation')}
                </label>
                <div className="input-item">
                  <input
                    type="text"
                    className={`form-input ${relationError ? 'error' : ''}`}
                    placeholder={t('sign-up-screen:form.placeholder.relation')}
                    value={relation}
                    onChange={this.handleRelationChange}
                    required
                  />
                </div>
              </div>
            </form>

            {/* Message Section */}
            <div className="message-container">
              <p className="message-text">
                {t('sign-up-screen:message')}
              </p>
            </div>

            {/* Sign Up Button */}
            <div className="button-container">
              <button
                type="button"
                className="signup-button comman-btn w-100"
                onClick={this.handleSubmit}
                disabled={registerFetching}
              >
                <span className="button-text uppercase">
                  {registerFetching ? t('sign-up-screen:button.signing-up') : t('sign-up-screen:button.sign-up')}
                </span>
              </button>
            </div>

          </div>
        </div>

        <AlertBox
          title={t('sign-up-screen:alert.success.title')}
          message={t('sign-up-screen:alert.success.message')}
          okAction={this.registerSuccessPopupClose}
          isVisible={this.state.registerSuccessPopupOpen || false}
          onClose={this.registerSuccessPopupClose}
        />

      </div>
    );
  }
}

// Add Redux connection
const mapStateToProps = (state: any) => {
  return {
    registerFetching: RegisterSelectors.isFetching(state),
    registerSuccess: RegisterSelectors.isSuccess(state),
    registerError: RegisterSelectors.hasError(state),
    registerStatus: RegisterSelectors.getStatus(state)
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    register: (email: string, childName: string, parentName: string, relation: string) =>
      dispatch(
        RegisterActions.registerRequest(email, childName, parentName, relation)
      ),
    resetData: () => dispatch(RegisterActions.resetData()),
  };
};

// Wrapper component to provide navigation props
const SignUpPageWithNavigation: React.FC<WithTranslation> = (props) => {
  const navigate = useNavigate();
  
  const handleNavigate = (path: string) => {
    navigate(path);
  };
  
  const handleGoBack = () => {
    navigate(-1);
  };
  
  return (
    <SignUpPage 
      {...props}
      navigate={handleNavigate}
      goBack={handleGoBack}
    />
  );
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(SignUpPageWithNavigation));
